// AnalisisCalidadListado.js
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { RefreshCw, X, Search, FileSpreadsheet } from "lucide-react";
import dayjs from "dayjs";
import { useNavigate, Outlet, useSearchParams, useMatch } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Select } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { DataTable } from "./components/ui/data-table";
import { DateRangePicker } from "./components/ui/date-range-picker";
import { toast } from "./lib/toast";
import apiUrl from "./config/config";

/* =================== Config común =================== */
const token =
  "Bearer 0j2whpl3*s+2/j-hCl!!-5A-8Corubp5[gqzhGrA.b*hBdO[+r.yGIFpvpOqv";

const EP_PRODUCTORES = `${apiUrl}/ServiceFamilyGrowers.asmx/GetFamilyProductores`;
const EP_VARIEDADES  = `${apiUrl}/ServiceFamilyGrowers.asmx/GetFamilyVariedades`;
const EP_LISTADO     = `${apiUrl}/ServiceFamilyGrowers.asmx/GetFamilylistadoAnalisisCalidad`;

const parseResponse = (data) => {
  try {
    if (data && typeof data === "object") {
      if ("d" in data) {
        return typeof data.d === "string" ? JSON.parse(data.d) : data.d;
      }
      return data;
    }
    let text = String(data ?? "");
    let cleaned = text
      .replace(/\{\s*"d"\s*:\s*null\s*\}\s*$/i, "")
      .replace(/,\s*([}\]])/g, "$1");
    const s = cleaned.indexOf("{");
    const e = cleaned.lastIndexOf("}");
    if (s !== -1 && e !== -1 && e > s) cleaned = cleaned.slice(s, e + 1);
    return JSON.parse(cleaned);
  } catch {
    return null;
  }
};

const trimStr = (v) => (typeof v === "string" ? v.trim() : v);

/* ========= Cache en sessionStorage ========= */
const CACHE_PREFIX = "AC_LIST_V1";
const CACHE_MAX_AGE_MS = 10 * 60 * 1000;

const cacheKeyFromState = (range, idProductor, idVariedad, estado, searchText) => {
  const [from, to] = range || [];
  return [
    CACHE_PREFIX,
    from ? from.format("YYYY-MM-DD") : "",
    to ? to.format("YYYY-MM-DD") : "",
    String(idProductor || 0),
    String(idVariedad || 0),
    String(estado || ""),
    String(searchText || "").trim().toLowerCase(),
  ].join("|");
};

const saveCache = (key, rows) => {
  try { sessionStorage.setItem(key, JSON.stringify({ rows, ts: Date.now() })); } catch {}
};

const loadCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !Array.isArray(obj.rows)) return null;
    if (Date.now() - Number(obj.ts || 0) > CACHE_MAX_AGE_MS) return null;
    return obj.rows;
  } catch { return null; }
};

const clearAllListCache = () => {
  try {
    Object.keys(sessionStorage).forEach((k) => {
      if (k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
    });
  } catch {}
};

const renderEstadoBadge = (estado, estadoNombre) => {
  if (!estadoNombre) return null;
  const variantMap = { "1": "gold", "2": "blue", "3": "green", "4": "red" };
  return <Badge variant={variantMap[estado] || "default"}>{estadoNombre}</Badge>;
};

const AnalisisCalidadListado = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [hydrated, setHydrated] = useState(false);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rowsRaw, setRowsRaw] = useState([]);

  const [range, setRange] = useState([dayjs().subtract(30, "day"), dayjs()]);
  const [idProductor, setIdProductor] = useState(0);
  const [idVariedad, setIdVariedad] = useState(0);
  const [estado, setEstado] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchTextDebounced, setSearchTextDebounced] = useState("");

  const [loadingProd, setLoadingProd] = useState(false);
  const [productores, setProductores] = useState([{ value: 0, label: "Todos" }]);

  const [loadingVar, setLoadingVar] = useState(false);
  const [variedades, setVariedades] = useState([{ value: 0, label: "Todas" }]);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  const isDetail = !!useMatch("/dashboard/calidad/analisis/:id");
  const paramsRef = useRef("");

  useEffect(() => {
    const t = setTimeout(
      () => setSearchTextDebounced(searchText.trim().toLowerCase()),
      200
    );
    return () => clearTimeout(t);
  }, [searchText]);

  const normalize = (arr) =>
    (Array.isArray(arr) ? arr : []).map((r, i) => {
      const productor  = trimStr(r.productor);
      const variedad   = trimStr(r.variedad);
      const encargado  = trimStr(r.respomsable || r.responsable);
      const estadoNom  = trimStr(r.estadonombre);
      const estadoCode = String(r.estado ?? "").trim();
      const fecha      = r.fecha ? dayjs(r.fecha).format("YYYY-MM-DD") : "";
      const haystack = [productor ?? "", variedad ?? "", encargado ?? "", estadoNom ?? "", fecha ?? ""]
        .join(" ").toLowerCase();
      return { key: r.id ?? i, id: r.id, fecha, productor, variedad, encargado, estadoNombre: estadoNom, estado: estadoCode, _haystack: haystack };
    });

  const fetchListado = useCallback(async () => {
    try {
      if (!range || range.length !== 2) {
        toast.error("Selecciona un rango de fechas.");
        return;
      }

      const fechaDesde = dayjs(range[0]).format("YYYY-MM-DD");
      const fechaHasta = dayjs(range[1]).format("YYYY-MM-DD");
      const idProd = Number(idProductor ?? 0);
      const idVar = Number(idVariedad ?? 0);
      const est = String(estado || "");

      const k = cacheKeyFromState(range, idProd, idVar, est, searchText);
      const cached = loadCache(k);
      if (cached && cached.length) {
        setRowsRaw(cached);
        setPagination((p) => ({ ...p, current: 1 }));
        return;
      }

      setLoading(true);

      const qs = new URLSearchParams();
      qs.append("fechaDesde", fechaDesde);
      qs.append("fechaHasta", fechaHasta);
      qs.append("idProductor", String(idProd));
      qs.append("idVariedad", String(idVar));
      if (est) qs.append("estado", est);
      if ((searchText || "").trim()) qs.append("q", (searchText || "").trim());

      const res = await fetch(`${EP_LISTADO}?${qs.toString()}`, {
        method: "GET",
        headers: { Authorization: token, Accept: "application/json" },
        credentials: "omit",
      });

      const ct = res.headers.get("content-type") || "";
      const raw = ct.includes("application/json") || ct.includes("text/json")
        ? await res.json().catch(() => null)
        : await res.text();

      if (!res.ok) {
        const msg = typeof raw === "string" ? raw : JSON.stringify(raw);
        throw new Error(msg || `HTTP ${res.status}`);
      }

      const payload = parseResponse(raw) ?? (raw?.d ? JSON.parse(raw.d) : raw);
      const list = Array.isArray(payload) ? payload : payload?.Monitoreo || payload?.data || [];
      const rows = normalize(list);

      setRowsRaw(rows);
      setPagination((p) => ({ ...p, current: 1 }));
      saveCache(k, rows);
    } catch (error) {
      console.error(error);
      toast.error("Error al consultar análisis de calidad.");
    } finally {
      setLoading(false);
    }
  }, [range, idProductor, idVariedad, estado, searchText]);

  const fetchProductores = useCallback(async () => {
    try {
      setLoadingProd(true);
      const res = await fetch(EP_PRODUCTORES, { method: "GET", headers: { Authorization: token, Accept: "application/json" }, credentials: "omit" });
      const ct = res.headers.get("content-type") || "";
      const raw = ct.includes("application/json") || ct.includes("text/json") ? await res.json().catch(() => null) : await res.text();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = parseResponse(raw) ?? (raw?.d ? JSON.parse(raw.d) : raw);
      const list = Array.isArray(payload) ? payload : payload?.data || [];
      const opts = [{ value: 0, label: "Todos" }].concat(
        list.map((x) => ({ value: Number(x.id || x.idproductor || 0), label: String(x.nombre || "").trim() }))
      );
      setProductores(opts);
    } catch (e) {
      console.error(e);
      toast.error("No se pudieron cargar los productores.");
    } finally {
      setLoadingProd(false);
    }
  }, []);

  const fetchVariedades = useCallback(async () => {
    try {
      setLoadingVar(true);
      const res = await fetch(EP_VARIEDADES, { method: "GET", headers: { Authorization: token, Accept: "application/json" }, credentials: "omit" });
      const ct = res.headers.get("content-type") || "";
      const raw = ct.includes("application/json") || ct.includes("text/json") ? await res.json().catch(() => null) : await res.text();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = parseResponse(raw) ?? (raw?.d ? JSON.parse(raw.d) : raw);
      const list = Array.isArray(payload) ? payload : payload?.data || [];
      const opts = [{ value: 0, label: "Todas" }].concat(
        list.map((x) => ({ value: Number(x.id || 0), label: String(x.nombre || "").trim() }))
      );
      setVariedades(opts);
    } catch (e) {
      console.error(e);
      toast.error("No se pudieron cargar las variedades.");
    } finally {
      setLoadingVar(false);
    }
  }, []);

  const filteredRows = useMemo(() => {
    if (!searchTextDebounced) return rowsRaw;
    return rowsRaw.filter((r) => r._haystack?.includes(searchTextDebounced));
  }, [rowsRaw, searchTextDebounced]);

  useEffect(() => {
    const d1 = searchParams.get("d1");
    const d2 = searchParams.get("d2");
    const pid = Number(searchParams.get("prod") || 0);
    const vid = Number(searchParams.get("var") || 0);
    const est = searchParams.get("est") || "";
    const q   = searchParams.get("q") || "";
    const pg  = Number(searchParams.get("pg") || 1);
    const ps  = Number(searchParams.get("ps") || 20);

    if (d1 && d2) setRange([dayjs(d1), dayjs(d2)]);
    setIdProductor(pid);
    setIdVariedad(vid);
    setEstado(est);
    setSearchText(q);
    setPagination({ current: pg || 1, pageSize: ps || 20 });
    setHydrated(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!range || range.length !== 2) return;
    const [from, to] = range;
    paramsRef.current = new URLSearchParams({
      d1: from.format("YYYY-MM-DD"),
      d2: to.format("YYYY-MM-DD"),
      prod: String(idProductor || 0),
      var: String(idVariedad || 0),
      est: String(estado || ""),
      q: searchText || "",
      pg: String(pagination.current || 1),
      ps: String(pagination.pageSize || 20),
    }).toString();
  }, [range, idProductor, idVariedad, estado, searchText, pagination]);

  useEffect(() => {
    if (!hydrated || !range || range.length !== 2 || isDetail) return;
    const [from, to] = range;
    setSearchParams(
      { d1: from.format("YYYY-MM-DD"), d2: to.format("YYYY-MM-DD"), prod: String(idProductor || 0), var: String(idVariedad || 0), est: String(estado || ""), q: searchText || "", pg: String(pagination.current || 1), ps: String(pagination.pageSize || 20) },
      { replace: true }
    );
  }, [hydrated, range, idProductor, idVariedad, estado, searchText, pagination, setSearchParams, isDetail]);

  useEffect(() => {
    if (!hydrated) return;
    fetchProductores();
    fetchVariedades();
    const k = cacheKeyFromState(range, idProductor, idVariedad, estado, searchText);
    const cached = loadCache(k);
    if (cached && cached.length) {
      setRowsRaw(cached);
      return;
    }
    fetchListado();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated]);

  const handleVer = useCallback(
    (id) => navigate(`/dashboard/calidad/analisis/${id}?${paramsRef.current}`),
    [navigate]
  );

  const columns = useMemo(
    () => [
      { title: "ID", dataIndex: "id", width: 80, sorter: (a, b) => (a.id || 0) - (b.id || 0), defaultSortOrder: "descend" },
      { title: "Fecha", dataIndex: "fecha", width: 120 },
      { title: "Productor", dataIndex: "productor", ellipsis: true, width: 240 },
      { title: "Variedad", dataIndex: "variedad", ellipsis: true, width: 170 },
      { title: "Responsable", dataIndex: "encargado", ellipsis: true, width: 190 },
      { title: "Estado", dataIndex: "estadoNombre", width: 160, render: (_, r) => renderEstadoBadge(r.estado, r.estadoNombre) },
      {
        title: "Acciones",
        key: "actions",
        fixed: "right",
        width: 120,
        render: (_, r) => (
          <Button variant="link" size="sm" onClick={() => handleVer(r.id)}>
            Ver
          </Button>
        ),
      },
    ],
    [handleVer]
  );

  const handleExportExcel = useCallback(async () => {
    try {
      const XLSX = await import("xlsx");
      const data = filteredRows.map((r) => ({
        ID: r.id, Fecha: r.fecha, Productor: r.productor, Variedad: r.variedad,
        Responsable: r.encargado, Estado: r.estadoNombre,
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const headers = Object.keys(data[0] || {});
      ws["!cols"] = headers.map((h) => ({ wch: Math.max(h.length, ...data.map((row) => (row[h] ? String(row[h]).length : 0))) + 2 }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "AnalisisCalidad");
      XLSX.writeFile(wb, `AnalisisCalidad_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo exportar a Excel.");
    }
  }, [filteredRows]);

  const handleLimpiar = () => {
    setRange([dayjs().subtract(30, "day"), dayjs()]);
    setIdProductor(0);
    setIdVariedad(0);
    setEstado("");
    setSearchText("");
    setRowsRaw([]);
    setPagination({ current: 1, pageSize: 20 });
    setSearchParams({}, { replace: true });
    clearAllListCache();
  };

  return (
    <div className="bg-white p-4 rounded-xl">
      {/* Barra de filtros */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-900 m-0">Análisis de Calidad</h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Buscar por productor / variedad / responsable"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-9 w-72"
            />
            {searchText && (
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={() => setSearchText("")}>
                <X className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
          </div>
          <DateRangePicker
            value={range}
            onChange={(vals) => setRange(vals || [null, null])}
            format="YYYY-MM-DD"
            allowClear={false}
          />
          <Select
            loading={loadingProd}
            options={productores}
            value={idProductor}
            onChange={(v) => setIdProductor(Number(v) || 0)}
            placeholder="Selecciona productor"
            style={{ width: 260 }}
            showSearch
            allowClear={false}
            optionFilterProp="label"
          />
          <Select
            loading={loadingVar}
            options={variedades}
            value={idVariedad}
            onChange={(v) => setIdVariedad(Number(v) || 0)}
            placeholder="Selecciona variedad"
            style={{ width: 240 }}
            showSearch
            allowClear={false}
            optionFilterProp="label"
          />
          <Select
            value={estado}
            onChange={(v) => setEstado(v ?? "")}
            placeholder="Estado"
            style={{ width: 180 }}
            allowClear
            options={[
              { value: "", label: "Todos" },
              { value: "1", label: "Pendiente" },
              { value: "2", label: "En Revisión" },
              { value: "3", label: "Aprobado" },
              { value: "4", label: "Rechazado" },
            ]}
          />
          <Button onClick={fetchListado} loading={loading}>
            <RefreshCw className="h-4 w-4" /> Buscar
          </Button>
          <Button variant="outline" onClick={handleLimpiar}>
            <X className="h-4 w-4" /> Limpiar
          </Button>
          <Button variant="outline" onClick={handleExportExcel}>
            <FileSpreadsheet className="h-4 w-4" /> Exportar Excel
          </Button>
        </div>
      </div>

      {/* Tabla (oculta en detalle) */}
      <div style={{ display: isDetail ? "none" : "block" }}>
        <DataTable
          loading={loading}
          columns={columns}
          data={filteredRows}
          rowKey="id"
          bordered
          scroll={{ x: 1100 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total, [start, end]) => `${start}-${end} de ${total} registros`,
          }}
          onPaginationChange={(page, pageSize) => setPagination({ current: page, pageSize })}
        />
      </div>

      <Outlet />
    </div>
  );
};

export default AnalisisCalidadListado;
